import { defineResource, type Tone } from '@tnn/core'
import { z } from 'zod'
import { departmentStatuses } from './resources'

/**
 * Time and attendance (เวลาทำงาน): when each person is meant to work (schedules by position or
 * branch, company holidays: on the day, or the day after on a schedule that runs a day behind),
 * when they came and left (scanning the
 * branch's QR with their phone; a counter or lead records it for someone without a phone; a
 * forgotten scan is corrected with approval), and overtime asked for ahead and approved.
 * Times are Thailand's (UTC+7 all year); every day below is a Thai date.
 */

export const weekdays = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const
export type Weekday = (typeof weekdays)[number]

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/
const time = () => z.string().regex(HHMM)
const BANGKOK_MS = 7 * 3_600_000

/** The Thai date of an instant (YYYY-MM-DD) */
export function bangkokDate(at: Date | string): string {
  return new Date(new Date(at).getTime() + BANGKOK_MS).toISOString().slice(0, 10)
}

/** The Thai clock time of an instant (HH:mm) */
export function bangkokTime(at: Date | string): string {
  return new Date(new Date(at).getTime() + BANGKOK_MS).toISOString().slice(11, 16)
}

/** The instant of a Thai date and clock time */
export function atBangkok(date: string, hhmm: string): Date {
  return new Date(Date.parse(`${date}T${hhmm}:00Z`) - BANGKOK_MS)
}

export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

export function weekdayOf(date: string): Weekday {
  return weekdays[new Date(`${date}T00:00:00Z`).getUTCDay()] as Weekday
}

/**
 * hr.schedule (เวลาทำงาน): hours and weekly days off. A person's is their own (hr.employee.scheduleId),
 * else the one for their position (and branch), else the one for their branch, else the default.
 * An end before the start runs past midnight (a night shift).
 */
export const schedule = defineResource({
  app: 'hr',
  name: 'schedule',
  path: 'schedules',
  schema: z.object({
    name: z.string().trim().min(1).max(80),
    startTime: time(),
    endTime: time(),
    /** When the break is (both or neither): a window inside the hours */
    breakStart: time().nullish(),
    breakEnd: time().nullish(),
    /** How long the break is; less than its window when people take it in turns. Empty: the window, else 60 */
    breakMinutes: z.number().int().min(0).max(240).nullish(),
    /** Minutes after the start that do not yet count as late */
    graceMinutes: z.number().int().min(0).max(120).default(5),
    daysOff: z.array(z.enum(weekdays)).default(['sun']),
    /**
     * Company holidays a day later: a destination branch gets on the holiday what the origin sent the day before, so it
     * works the holiday and is off the day after (see holidayDateFor)
     */
    holidaysNextDay: z.boolean().default(false),
    /** For these positions (with the branches below, if any) */
    positionIds: z.array(z.string()).default([]),
    /** For these branches */
    branchIds: z.array(z.string()).default([]),
    /** For whoever no other schedule fits */
    isDefault: z.boolean().default(false),
    status: z.enum(departmentStatuses).default('active'),
    notes: z.string().max(500).nullish(),
  }),
  fields: {
    name: { search: true, list: { priority: 1 }, form: { span: 12 } },
    startTime: { type: 'time', list: { width: 100, priority: 1 }, form: { span: 3 } },
    endTime: { type: 'time', list: { width: 100, priority: 1 }, form: { span: 3 } },
    breakStart: { type: 'time', list: { width: 100, priority: 2 }, form: { span: 3 } },
    breakEnd: { type: 'time', list: { width: 100, priority: 2 }, form: { span: 3 } },
    breakMinutes: { type: 'integer', list: { priority: 3, hidden: true }, form: { span: 3 } },
    graceMinutes: { type: 'integer', list: { priority: 3, hidden: true }, form: { span: 3 } },
    daysOff: { type: 'multiselect', options: weekdays, optionLabels: 'hr:weekday', list: { priority: 1 }, form: { span: 12 } },
    holidaysNextDay: { list: { width: 150, priority: 2 }, form: { span: 12 } },
    positionIds: { type: 'multiselect', ref: { resource: 'hr.position' }, list: { priority: 2 }, form: { span: 6, section: 'who' } },
    branchIds: { type: 'multiselect', ref: { resource: 'company.branch' }, list: { priority: 2 }, form: { span: 6, section: 'who' } },
    isDefault: { list: { width: 110, priority: 2 }, form: { span: 6, section: 'who' } },
    status: {
      filter: true,
      tones: { active: 'success', inactive: 'neutral' },
      list: { width: 120, priority: 1 },
      form: { span: 6, section: 'who' },
    },
    notes: { type: 'textarea', list: false, form: { span: 12, section: 'who' } },
  },
  title: 'name',
  columns: [
    'name',
    'startTime',
    'endTime',
    'breakStart',
    'breakEnd',
    'daysOff',
    'holidaysNextDay',
    'positionIds',
    'branchIds',
    'isDefault',
    'status',
  ],
  sections: ['main', 'who'],
  defaultSort: 'name',
  // Importing the file again updates its schedules rather than adding them twice
  importKey: 'name',
  card: { title: 'name', meta: ['startTime', 'endTime'], status: 'status' },
})

/** What a shift is matched to people by */
interface ShiftMatch {
  id: string
  status: string
  positionIds: readonly string[]
  branchIds: readonly string[]
  isDefault: boolean
}

/**
 * The shift someone works by, of `shifts` (the active ones): their own, else the one for their position (for their
 * branch before one for every branch), else the one for their branch, else the default; none: undefined
 */
export function shiftOf<S extends ShiftMatch>(
  person: { scheduleId?: string | null; positionId?: string | null; branchId?: string | null },
  shifts: readonly S[],
): S | undefined {
  const active = shifts.filter((s) => s.status === 'active')
  const { scheduleId, positionId, branchId } = person
  const own = scheduleId ? active.find((s) => s.id === scheduleId) : undefined
  const forJob = positionId ? active.filter((s) => s.positionIds.includes(positionId)) : []
  const forPosition = forJob.find((s) => !!branchId && s.branchIds.includes(branchId)) ?? forJob.find((s) => !s.branchIds.length)
  const forBranch = branchId ? active.find((s) => !s.positionIds.length && s.branchIds.includes(branchId)) : undefined
  return own ?? forPosition ?? forBranch ?? active.find((s) => s.isDefault)
}

/** hr.holiday (วันหยุดบริษัท): days off for everyone, a day later on a schedule a day behind; working one is holiday work (see overtimeSplit) */
export const holiday = defineResource({
  app: 'hr',
  name: 'holiday',
  path: 'holidays',
  schema: z.object({
    date: z.iso.date(),
    name: z.string().trim().min(1).max(100),
    notes: z.string().max(300).nullish(),
  }),
  fields: {
    date: { filter: true, list: { width: 140, priority: 1 }, form: { span: 4 }, import: { aliases: ['วันที่'] } },
    name: { search: true, list: { priority: 1 }, form: { span: 8 }, import: { aliases: ['ชื่อวันหยุด', 'วันหยุด'] } },
    notes: { type: 'textarea', list: false, form: { span: 12 } },
  },
  title: 'name',
  defaultSort: 'date',
  importKey: 'date',
  card: { title: 'name', meta: ['date'] },
})

export const timeEntryKinds = ['in', 'out'] as const
/**
 * `qr`: scanned at a branch's clock screen; `selfie`: a photo of oneself and where the phone was (before only the QR
 * counted: no new ones); `recorded`: written for someone at the counter; `correction`: a forgotten scan, approved first
 */
export const timeEntryMethods = ['qr', 'selfie', 'recorded', 'correction'] as const
/**
 * `replaced`: was `ok`, no longer counts since HR or a lead set that day's time in its place, or its person switched
 * the scan to the other kind
 */
export const timeEntryStatuses = ['ok', 'pending', 'rejected', 'replaced'] as const
/** `outside`: farther from the branch than its radius; `noLocation`: the phone gave no position */
export const timeEntryFlags = ['outside', 'noLocation'] as const

/**
 * hr.timeEntry (บันทึกเวลา): one scan in or out, never edited. Made by the server: a scan of a
 * branch's clock screen (`POST /api/me/clock`, in or out as the system picks), one recorded for
 * someone (`recordFor`), or a correction the person asked for (`pending` until approved by whoever
 * approves their leave). Only `ok` ones count; setting a day's time for someone (`recordFor`) marks
 * the ones it takes the place of `replaced`, kept to look back on, and so does a person switching
 * their scan just made to the other kind.
 */
export const timeEntry = defineResource({
  app: 'hr',
  name: 'timeEntry',
  path: 'time-entries',
  schema: z.object({
    employeeId: z.string().min(1),
    kind: z.enum(timeEntryKinds),
    at: z.iso.datetime(),
    reason: z.string().trim().max(300).nullish(),
  }),
  fields: {
    employeeId: { type: 'ref', ref: { resource: 'hr.employee' }, filter: true, list: { priority: 1 } },
    kind: { filter: true, list: { width: 90, priority: 1 } },
    at: { type: 'datetime', list: { width: 160, priority: 1 } },
    reason: { list: { priority: 2 } },
    date: { readOnly: true, type: 'date', filter: true, list: { width: 130, priority: 2 } },
    method: { readOnly: true, type: 'select', options: timeEntryMethods, filter: true, list: { width: 120, priority: 2 } },
    status: {
      readOnly: true,
      type: 'select',
      options: timeEntryStatuses,
      filter: true,
      tones: { ok: 'success', pending: 'warning', rejected: 'danger', replaced: 'neutral' },
      list: { width: 110, priority: 1 },
    },
    flags: { readOnly: true, type: 'multiselect', options: timeEntryFlags, list: { priority: 2 } },
    /** Where it was scanned (the branch of the QR), else the person's */
    branchId: { readOnly: true, type: 'ref', ref: { resource: 'company.branch' }, filter: true, list: { priority: 2 } },
    latitude: { readOnly: true, type: 'number', list: false },
    longitude: { readOnly: true, type: 'number', list: false },
    /** Metres, as the phone reported it */
    accuracy: { readOnly: true, type: 'number', list: false },
    /** Metres from the branch (when the branch has a location) */
    distance: { readOnly: true, type: 'number', list: { priority: 3, hidden: true } },
    /** A selfie's photo (older entries): whoever may read the entry sees it; never an assistant's (personal data) */
    photoFileId: { readOnly: true, type: 'file', list: false, import: false, export: false },
    summary: { readOnly: true, search: true, list: false },
    userId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: false },
    /** The employee's home branch, for a lead's scope (where they scanned is `branchId`) */
    homeBranchId: { readOnly: true, type: 'ref', ref: { resource: 'company.branch' }, list: false },
    recordedByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true } },
    reviewedByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true } },
    reviewedAt: { readOnly: true, type: 'datetime', list: false },
    rejectReason: { readOnly: true, list: false },
  },
  title: 'summary',
  columns: ['at', 'employeeId', 'kind', 'method', 'status', 'branchId', 'flags', 'reason', 'recordedByUserId'],
  defaultSort: '-at',
  // Never edited: corrections are entries of their own. Recorded ones, set times and corrections go through /api/hr
  actions: ['read', 'export', 'recordFor', 'approve'],
  ownerField: ['userId', 'recordedByUserId'],
  branchFields: ['homeBranchId'],
  teamField: 'employeeId',
})

export const clockDisplayStatuses = ['active', 'revoked'] as const

/**
 * hr.clockDisplay (จอลงเวลา): a phone or tablet at a branch showing the time clock's QR, good once
 * and new as soon as someone scans it, for staff to scan with their own phones: the only way they
 * clock in themselves. A device on the screen site (screen.<domain>) is paired to it by a code HR
 * shows (`pair`), with no account signed in; pairing another replaces it, and revoking stops it
 * for good. HR's alone, at every branch (since 4 Nov 2026 no lead has them).
 */
export const clockDisplay = defineResource({
  app: 'hr',
  name: 'clockDisplay',
  path: 'clock-displays',
  schema: z.object({
    name: z.string().trim().min(1).max(80),
    branchId: z.string().min(1),
    notes: z.string().max(300).nullish(),
  }),
  fields: {
    name: { search: true, list: { priority: 1 }, form: { span: 6 } },
    branchId: {
      type: 'ref',
      ref: { resource: 'company.branch', where: { status: 'active' } },
      filter: true,
      list: { priority: 1 },
      form: { span: 6 },
    },
    notes: { type: 'textarea', list: false, form: { span: 12 } },
    /** When the device showing it now was paired; empty until one is */
    pairedAt: { readOnly: true, type: 'datetime', list: { width: 160, priority: 2 } },
    status: {
      readOnly: true,
      type: 'select',
      options: clockDisplayStatuses,
      filter: true,
      tones: { active: 'success', revoked: 'neutral' },
      list: { width: 120, priority: 1 },
    },
    /** When the screen last fetched a code: a screen that went dark shows here */
    lastSeenAt: { readOnly: true, type: 'datetime', list: { width: 160, priority: 1 } },
    createdByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true } },
    createdAt: { readOnly: true, type: 'datetime', list: { priority: 3, hidden: true } },
  },
  title: 'name',
  columns: ['name', 'branchId', 'status', 'pairedAt', 'lastSeenAt', 'createdByUserId'],
  defaultSort: 'name',
  // `pair`: show the code a device pairs with; `revoke`: its device stops for good (a lost phone), a new screen is a new display
  actions: ['read', 'create', 'update', 'delete', 'pair', 'revoke'],
  branchFields: ['branchId'],
  card: { title: 'name', subtitle: 'branchId', meta: ['lastSeenAt'], status: 'status' },
})

/** OT hours of a day: more than none, up to a day, by the quarter hour */
export const overtimeHoursSchema = z.number().positive().max(24).multipleOf(0.25)

/**
 * hr.overtime (OT): the hours a person worked overtime on a day, one per person per day, filled in on ตาราง OT by
 * their branch lead or HR (`record`), counted as they are — nobody asks or approves. How a day's hours are paid depends
 * on that day for the person (`overtimeSplit`): a working day 1.5×, a day off or holiday 2× up to the normal hours and
 * 3× beyond them. Written only through ตาราง OT (`PUT /api/hr/overtime/sheet`), so the record page is read-only.
 */
export const overtime = defineResource({
  app: 'hr',
  name: 'overtime',
  path: 'overtime',
  schema: z.object({
    employeeId: z.string().min(1),
    date: z.iso.date(),
    hours: overtimeHoursSchema,
  }),
  fields: {
    employeeId: { type: 'ref', ref: { resource: 'hr.employee' }, filter: true, list: { priority: 1 }, form: { span: 6 } },
    date: { filter: true, list: { width: 130, priority: 1 }, form: { span: 3 } },
    hours: { type: 'number', list: { width: 90, priority: 1 }, form: { span: 3 } },
    summary: { readOnly: true, search: true, list: false },
    branchId: { readOnly: true, type: 'ref', ref: { resource: 'company.branch' }, filter: true, list: { priority: 2 } },
  },
  title: 'summary',
  columns: ['date', 'employeeId', 'hours', 'branchId'],
  defaultSort: '-date',
  actions: ['read', 'export', 'record'],
  branchFields: ['branchId'],
  teamField: 'employeeId',
  card: { title: 'summary', meta: ['date', 'hours'] },
})

export interface ScheduleValues {
  startTime: string
  endTime: string
  breakStart?: string | null
  breakEnd?: string | null
  breakMinutes?: number | null
  graceMinutes?: number | null
  daysOff: readonly Weekday[]
  holidaysNextDay?: boolean | null
}

/** The date whose company holiday a schedule takes on a date: its own, or the day before's on a schedule a day behind */
export const holidayDateFor = (date: string, plan: Pick<ScheduleValues, 'holidaysNextDay'>) =>
  plan.holidaysNextDay ? addDays(date, -1) : date

/** One person's day as planned: working or not (and why), from when to when */
export interface WorkDay {
  date: string
  working: boolean
  /** Not working because of… */
  off?: 'dayOff' | 'holiday'
  holiday?: string
  start: Date | null
  end: Date | null
  /** The break's window on a working day by the schedule (none without one) */
  breakStart: Date | null
  breakEnd: Date | null
  graceMinutes: number
  /** Hours of a normal working day by the schedule (break out), also on days off: holiday work is counted against it */
  normalHours: number
}

const minutesOf = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5))

/** Minutes from one clock time to the next, past midnight when it ends earlier than it starts */
function span(start: string, end: string): number {
  const m = minutesOf(end) - minutesOf(start)
  return m > 0 ? m : m + 24 * 60
}

/** Minutes from the start of the hours to a clock time in them (a night shift's past midnight too) */
const fromStart = (start: string, at: string) => (minutesOf(at) - minutesOf(start) + 24 * 60) % (24 * 60)

/**
 * A schedule's break window, in minutes from the start of its hours, if it has one: `{ from, to, minutes }`; `error`
 * names what is wrong with it (one end only, outside the hours, ending before it starts)
 */
export function breakWindow(plan: Pick<ScheduleValues, 'startTime' | 'endTime' | 'breakStart' | 'breakEnd'>) {
  if (!plan.breakStart && !plan.breakEnd) return null
  if (!plan.breakStart || !plan.breakEnd) return { error: 'oneEnd' as const }
  const from = fromStart(plan.startTime, plan.breakStart)
  const to = fromStart(plan.startTime, plan.breakEnd)
  if (from <= 0 || to <= from || to >= span(plan.startTime, plan.endTime)) return { error: 'outside' as const }
  return { from, to, minutes: to - from }
}

/** Hours of a normal day on a schedule: its hours less the break (not given: its window, else an hour) */
export function scheduleHours(plan: Pick<ScheduleValues, 'startTime' | 'endTime' | 'breakStart' | 'breakEnd' | 'breakMinutes'>) {
  const window = breakWindow(plan)
  const rest = plan.breakMinutes ?? (window && !('error' in window) ? window.minutes : 60)
  return Math.max(0, span(plan.startTime, plan.endTime) - rest) / 60
}

/** The start and end instants of hours on a date (the end on the next day for a night shift) */
function hoursOn(date: string, start: string, end: string): { start: Date; end: Date } {
  const from = atBangkok(date, start)
  return { start: from, end: new Date(from.getTime() + span(start, end) * 60_000) }
}

/** A person's planned day: a company holiday (the one `holidayDateFor` gives), else their weekly days off */
export function workDay(date: string, plan: ScheduleValues, options: { holiday?: string | null } = {}): WorkDay {
  const { holiday: holidayName } = options
  const normalHours = scheduleHours(plan)
  const base = { date, graceMinutes: plan.graceMinutes ?? 0, normalHours, breakStart: null, breakEnd: null }
  const off = (why: WorkDay['off']): WorkDay => ({
    ...base,
    working: false,
    off: why,
    holiday: holidayName ?? undefined,
    start: null,
    end: null,
  })
  const scheduled = (): WorkDay => {
    const hours = hoursOn(date, plan.startTime, plan.endTime)
    const window = breakWindow(plan)
    if (!window || 'error' in window) return { ...base, working: true, ...hours }
    const at = (minutes: number) => new Date(hours.start.getTime() + minutes * 60_000)
    return { ...base, working: true, ...hours, breakStart: at(window.from), breakEnd: at(window.to) }
  }
  if (holidayName) return off('holiday')
  if (plan.daysOff.includes(weekdayOf(date))) return off('dayOff')
  return scheduled()
}

export const dayStatuses = ['present', 'late', 'absent', 'leave', 'off', 'incomplete', 'upcoming'] as const
export type DayStatus = (typeof dayStatuses)[number]

/** How each kind of day is shown (status pills, calendar cells) */
export const dayStatusTones: Record<DayStatus, Tone> = {
  present: 'success',
  late: 'warning',
  absent: 'danger',
  leave: 'info',
  off: 'neutral',
  incomplete: 'warning',
  upcoming: 'neutral',
}

export interface DayAttendance {
  status: DayStatus
  firstIn: Date | null
  lastOut: Date | null
  /** After the start and its grace (counted from the start) */
  lateMinutes: number
  /** Before the end */
  earlyMinutes: number
  workedMinutes: number
  /** Someone who does not clock in (`hr.employee.clockFree`): their working day counted as come by the clock's absence */
  untimed?: true
}

/**
 * How a day went: on leave (a whole day), off, not come (absent, once the day's end has passed),
 * come without leaving (incomplete, four hours past the end), late, or present. A half-day leave
 * excuses its half: late is not counted on a morning off, leaving early not on an afternoon off.
 * Someone who does not clock in (`untimed`) is present on a working day once it has started, never
 * absent, late or out early, whatever they scanned.
 */
export function dayAttendance(input: {
  day: WorkDay
  /** Approved, `ok` entries of the day */
  entries: readonly { kind: 'in' | 'out'; at: Date | string }[]
  leave?: { half: 'full' | 'morning' | 'afternoon' } | null
  now: Date
  /** ไม่ต้องลงเวลา (`hr.employee.clockFree`) */
  untimed?: boolean
}): DayAttendance {
  const { day, leave, now } = input
  const ins = input.entries.filter((e) => e.kind === 'in').map((e) => new Date(e.at).getTime())
  const outs = input.entries.filter((e) => e.kind === 'out').map((e) => new Date(e.at).getTime())
  const firstIn = ins.length ? new Date(Math.min(...ins)) : null
  const lastOutTime = outs.length ? Math.max(...outs) : null
  const lastOut = lastOutTime != null && (!firstIn || lastOutTime > firstIn.getTime()) ? new Date(lastOutTime) : null
  const workedMinutes = firstIn && lastOut ? Math.round((lastOut.getTime() - firstIn.getTime()) / 60_000) : 0
  const result = { firstIn, lastOut, lateMinutes: 0, earlyMinutes: 0, workedMinutes }
  if (leave?.half === 'full') return { ...result, status: 'leave' }
  if (!day.working || !day.start || !day.end) return { ...result, status: firstIn ? 'present' : 'off' }
  if (input.untimed) return { ...result, untimed: true, status: now < day.start ? 'upcoming' : 'present' }
  if (!firstIn) return { ...result, status: now > day.end ? 'absent' : 'upcoming' }
  const late = Math.round((firstIn.getTime() - day.start.getTime()) / 60_000)
  const lateMinutes = leave?.half === 'morning' || late <= day.graceMinutes ? 0 : late
  const early = lastOut ? Math.round((day.end.getTime() - lastOut.getTime()) / 60_000) : 0
  const earlyMinutes = leave?.half === 'afternoon' ? 0 : Math.max(0, early)
  const status: DayStatus =
    !lastOut && now.getTime() > day.end.getTime() + 4 * 3_600_000 ? 'incomplete' : lateMinutes > 0 ? 'late' : 'present'
  return { ...result, lateMinutes, earlyMinutes, status }
}

/**
 * Overtime by how it is paid (Labour Protection Act): on a working day all of it is overtime
 * (1.5×); on a day off or holiday, up to the normal hours it is holiday work (2× all told: a
 * monthly salary already pays the day once), beyond them holiday overtime (3×).
 */
export function overtimeSplit(day: Pick<WorkDay, 'working' | 'normalHours'>, hours: number) {
  if (day.working) return { overtime: hours, holidayWork: 0, holidayOvertime: 0 }
  const holidayWork = Math.min(hours, day.normalHours)
  return { overtime: 0, holidayWork, holidayOvertime: Math.max(0, hours - holidayWork) }
}
